import React, { useState } from "react";
import { View, Text, StyleSheet, Image, TouchableOpacity, TextInput } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTheme } from "../../../context/ThemeContext";

const MemberCard = ({ member }) => {
	const theme = useTheme();
	const styles = useStyles();

	return (
		<View style={styles.memberCard}>
			<View style={styles.memberHeader}>
				<Image source={{ uri: member.avatar }} style={styles.avatar} />
				<View style={styles.memberInfo}>
					<Text style={styles.memberName}>{member.name}</Text>
					<Text style={styles.memberRole}>{member.role}</Text>
				</View>
				<TouchableOpacity style={styles.messageButton}>
					<Ionicons name="mail-outline" size={20} color={theme.colors.primary} />
				</TouchableOpacity>
			</View>

			<View style={styles.memberDetails}>
				<View style={styles.detailItem}>
					<Ionicons name="book-outline" size={16} color={theme.colors.textSecondary} />
					<Text style={styles.detailText}>Currently reading: {member.currentlyReading}</Text>
				</View>
				<View style={styles.detailItem}>
					<Ionicons name="trophy-outline" size={16} color={theme.colors.textSecondary} />
					<Text style={styles.detailText}>Goal: {member.readingGoal}</Text>
				</View>
				<View style={styles.detailItem}>
					<Ionicons name="calendar-outline" size={16} color={theme.colors.textSecondary} />
					<Text style={styles.detailText}>Joined {new Date(member.joinDate).toLocaleDateString()}</Text>
				</View>
			</View>

			<View style={styles.badges}>
				{member.badges.map((badge, index) => (
					<View key={index} style={styles.badge}>
						<Text style={styles.badgeText}>{badge}</Text>
					</View>
				))}
			</View>
		</View>
	);
};

const MemberDirectory = ({ members = [] }) => {
	const theme = useTheme();
	const styles = useStyles();
	const [searchQuery, setSearchQuery] = useState("");
	const [filter, setFilter] = useState("all");

	const filteredMembers = members.filter((member) => {
		const matchesSearch = member.name.toLowerCase().includes(searchQuery.toLowerCase());
		if (filter === "all") return matchesSearch;
		return matchesSearch && member.role.toLowerCase().includes(filter.toLowerCase());
	});

	return (
		<View style={styles.container}>
			<View style={styles.searchBar}>
				<Ionicons name="search-outline" size={20} color={theme.colors.textSecondary} style={styles.searchIcon} />
				<TextInput style={styles.searchInput} placeholder="Search members..." placeholderTextColor={theme.colors.textSecondary} value={searchQuery} onChangeText={setSearchQuery} />
			</View>

			<View style={styles.filters}>
				<TouchableOpacity style={[styles.filterButton, filter === "all" && styles.activeFilter]} onPress={() => setFilter("all")}>
					<Text style={[styles.filterText, filter === "all" && styles.activeFilterText]}>All Members</Text>
				</TouchableOpacity>
				<TouchableOpacity style={[styles.filterButton, filter === "leader" && styles.activeFilter]} onPress={() => setFilter("leader")}>
					<Text style={[styles.filterText, filter === "leader" && styles.activeFilterText]}>Club Leaders</Text>
				</TouchableOpacity>
				<TouchableOpacity style={[styles.filterButton, filter === "moderator" && styles.activeFilter]} onPress={() => setFilter("moderator")}>
					<Text style={[styles.filterText, filter === "moderator" && styles.activeFilterText]}>Moderators</Text>
				</TouchableOpacity>
			</View>

			<View style={styles.memberList}>
				{filteredMembers.map((member) => (
					<MemberCard key={member.id} member={member} />
				))}
			</View>
		</View>
	);
};

const useStyles = () => {
	const theme = useTheme();

	return StyleSheet.create({
		container: {
			flex: 1,
		},
		searchBar: {
			flexDirection: "row",
			alignItems: "center",
			backgroundColor: theme.colors.systemBackground.primary,
			borderRadius: theme.borderRadius.md,
			padding: theme.spacing.md,
			marginBottom: theme.spacing.md,
			...theme.shadows.sm,
		},
		searchIcon: {
			marginRight: theme.spacing.sm,
		},
		searchInput: {
			flex: 1,
			...theme.typography.body,
			color: theme.colors.text,
		},
		filters: {
			flexDirection: "row",
			gap: theme.spacing.sm,
			marginBottom: theme.spacing.md,
		},
		filterButton: {
			paddingVertical: theme.spacing.xs,
			paddingHorizontal: theme.spacing.sm,
			borderRadius: theme.borderRadius.pill,
			backgroundColor: theme.colors.systemBackground.primary,
		},
		activeFilter: {
			backgroundColor: theme.colors.primary,
		},
		filterText: {
			...theme.typography.footnote,
			color: theme.colors.textSecondary,
		},
		activeFilterText: {
			color: theme.colors.systemBackground.primary,
			fontWeight: "600",
		},
		memberList: {
			gap: theme.spacing.md,
		},
		memberCard: {
			backgroundColor: theme.colors.systemBackground.primary,
			borderRadius: theme.borderRadius.lg,
			padding: theme.spacing.md,
			...theme.shadows.sm,
		},
		memberHeader: {
			flexDirection: "row",
			alignItems: "center",
			marginBottom: theme.spacing.md,
		},
		avatar: {
			width: 48,
			height: 48,
			borderRadius: 24,
			marginRight: theme.spacing.md,
		},
		memberInfo: {
			flex: 1,
		},
		memberName: {
			...theme.typography.headline,
			color: theme.colors.text,
			marginBottom: theme.spacing.xs,
		},
		memberRole: {
			...theme.typography.subhead,
			color: theme.colors.textSecondary,
		},
		messageButton: {
			padding: theme.spacing.sm,
			borderRadius: theme.borderRadius.pill,
			backgroundColor: theme.colors.gray[6],
		},
		memberDetails: {
			gap: theme.spacing.sm,
			marginBottom: theme.spacing.md,
		},
		detailItem: {
			flexDirection: "row",
			alignItems: "center",
			gap: theme.spacing.sm,
		},
		detailText: {
			...theme.typography.subhead,
			color: theme.colors.textSecondary,
		},
		badges: {
			flexDirection: "row",
			flexWrap: "wrap",
			gap: theme.spacing.sm,
		},
		badge: {
			paddingVertical: theme.spacing.xs,
			paddingHorizontal: theme.spacing.sm,
			backgroundColor: theme.colors.gray[6],
			borderRadius: theme.borderRadius.pill,
		},
		badgeText: {
			...theme.typography.caption1,
			color: theme.colors.primary,
			fontWeight: "500",
		},
	});
};

export default MemberDirectory;
